/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "actions";
import MockOrderDetail from "components/elements/main/MockOrderDetail";
import { DATE_FORMAT, CURRENCY } from "config/constants";
import { getLocalDate, getTimeSlot, getImage, getImageFromURL, isMarketPlace, openUpiApp } from "helpers/utils";
import {
    Typography,
    Container,
    Box,
    Card,
    Avatar,
    CircularProgress,
    Button,
    Grid,
    Divider,
    CardContent,
    CardHeader,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import ShopHeaderMini from "components/elements/main/ShopHeaderMini";
import * as ActionTypes from "constants/ActionTypes";
import { Notification } from "components/Atoms";
import ImagesDownload from "components/elements/main/ImagesDownload";
import CameraButton from "components/elements/main/CameraButton";
import FileButton from "components/elements/main/FileButton";
import {dataURIToBlob, resizeFile} from "services/utils";

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BazaarAvatar from "components/BazaarAvatar";

const ShopOrderDetail = (props) => {
    const {
        serviceError, reqStatus, isLoading,
        orderdetail, getShopOrderDetail, userUpdateOrderStatus, checkPendingPayments, pendingPayments,
        bootupInfo, shopinfo, symbol, match, uploadPaymentScreenshot
    } = props;

    const [copySuccess, setCopySuccess] = useState('Copy');
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [paymentScreenshot, setPaymentScreenshot] = useState(null);
    const [uploading, setUploading] = useState(false);

    const { orderId } = match.params;
    const shopId = isMarketPlace() ? match.params.shopId : bootupInfo.appState.subdomain_shopId;

    const {
        products, shipping, order_id, order_type,
        total_order, total_price, total_shipping, shippingoption, total_tax, delivery_date, delivery_time,
        created_at: createdAt, status, payment
    } = orderdetail;

    useEffect(() => {
        getShopOrderDetail(orderId, shopId, !isMarketPlace());
    }, [orderId, shopId, getShopOrderDetail]);

    useEffect(() => {
        if (serviceError && reqStatus) {
            Notification.error({ message: "Error", description: serviceError.statusText });
        }
        if (reqStatus === ActionTypes.USER_UPDATE_ORDER_STATUS_SUCCESS || 
            reqStatus === ActionTypes.USER_UPLOAD_PAYMENT_SCREENSHOT_SUCCESS) {
            getShopOrderDetail(orderId, shopId, !isMarketPlace());
            checkPendingPayments(shopinfo.subdomain, false);
        }
    }, [serviceError, reqStatus, orderId, shopId, getShopOrderDetail]);

    const updateOrder = (newStatus) => {
        userUpdateOrderStatus({ order_id: orderdetail._id, status: newStatus, message: "" });
    };

    const copyToClipBoard = async (copyMe) => {
        try {
            await navigator.clipboard.writeText(copyMe);
            setCopySuccess('Copied!');
            setTimeout(() => setCopySuccess('Copy'), 2000);
        } catch (err) {
            setCopySuccess('Failed!');
        }
    };

    const handleUploadPaymentScreenshot = async (image) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('order_id', orderdetail._id);
            formData.append('image', image);
            
            await uploadPaymentScreenshot(formData);
            setPaymentScreenshot(image);
            setShowUploadDialog(false);
        } catch (error) {
            Notification.error({ message: "Error", description: "Failed to upload screenshot" });
        } finally {
            setUploading(false);
        }
    };

    const addCameraImage = (file) => {
        (async () => {
            const cameraimage = await resizeFile(dataURIToBlob("data:image/jpeg;base64,"+file));
            const blob = dataURIToBlob(cameraimage);
            handleUploadPaymentScreenshot(blob);
        })();
    };

    const addFileImage = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleUploadPaymentScreenshot(file);
        }
    };
    
    const renderUpiButtons = () => (
        <Grid container spacing={1} mt={1}>
            <Grid item xs={6}><Button fullWidth variant="contained" onClick={() => openUpiApp("gpay", shopinfo.paymentupi, shopinfo.title, order_id, order_id, total_order)}>GPay</Button></Grid>
            <Grid item xs={6}><Button fullWidth variant="contained" onClick={() => openUpiApp("phonepe", shopinfo.paymentupi, shopinfo.title, order_id, order_id, total_order)}>PhonePe</Button></Grid>
            <Grid item xs={6}><Button fullWidth variant="contained" onClick={() => openUpiApp("paytm", shopinfo.paymentupi, shopinfo.title, order_id, order_id, total_order)}>PayTM</Button></Grid>
            <Grid item xs={6}><Button fullWidth variant="contained" onClick={() => openUpiApp("default", shopinfo.paymentupi, shopinfo.title, order_id, order_id, total_order)}>Other</Button></Grid>
        </Grid>
    );

    const renderCustomerActions = () => {
        const actions = [];
        if ((status === "paymentpending" || status === "paid") && payment?.method === "upibased") {
                        actions.push(
                <Button 
                    sx={{ mb: 2 }}
                    key="upload" 
                    fullWidth 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<UploadFileIcon />}
                    onClick={() => setShowUploadDialog(true)}
                >
                    Upload Payment Screenshot
                </Button>
            );
        }
        
        if (payment?.screenshot) {
            actions.push(
                <img
                width="100%"
                src={getImageFromURL(payment.screenshot)}
                />
            );
        }
        
        if (status === "paymentpending" && payment?.method === "upibased") {
            actions.push(<Button key="paid" fullWidth variant="contained" color="primary" onClick={() => updateOrder("paid")}>I've Paid</Button>);
        }
        if (status === "readyforpickup") {
            actions.push(<Button key="pickup" fullWidth variant="contained" color="primary" onClick={() => updateOrder("pickedup")}>Mark as Picked Up</Button>);
        }
        if (["new", "accepted", "paymentpending"].includes(status)) {
            actions.push(<><Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1, mt: 4 }}>Don't want it anymore?</Typography><Button key="cancel" fullWidth variant="outlined" color="error" onClick={() => updateOrder("cancelled")}>Cancel Order</Button></>);
        }

        return actions.length > 0 ? <Box mt={2} display="grid" gap={1.5}>{actions}</Box> : null;
    };
    
    const renderStatusAndActions = () => {
        const statusMessages = {
            new: "Order placed, awaiting seller confirmation.",
            accepted: "Order accepted. Waiting for next step.",
            paymentpending: "Payment is pending. Click below for UPI payment.",
            paid: "Payment confirmed. Seller is processing your order.",
            inprogress: "Order is being packed by the seller.",
            readyforpickup: "Your order is ready for pickup!",
            pickedup: "Order picked up. Thank you!",
            readydelivery: "Ready for delivery. Courier will pick it up soon.",
            indelivery: "Your order is out for delivery.",
            delivered: "Order delivered successfully.",
            returned: "This order was returned.",
            rejected: "This order was rejected by the seller.",
            cancelled: "This order was cancelled."
        };

        return (
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader avatar={<ReceiptLongIcon sx={{ color: 'primary.main' }} />} title="Order Status" titleTypographyProps={{ fontWeight: 600 }} sx={{ bgcolor: 'grey.200' }} />
                <CardContent>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>{status}</Typography>
                    <Typography color="text.secondary">{statusMessages[status] || 'Status unknown.'}</Typography>
                    {renderPaymentInfo()}
                    {renderCustomerActions()}
                </CardContent>
            </Card>
        );
    };

    const renderPaymentInfo = () => {
        if (status === "paymentpending" && payment?.method === "upibased") {
            // The detailed UPI payment card is shown in this case
            return renderUpiButtons();
        }
        
        return (
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader 
                  avatar={<PaymentIcon sx={{ color: 'primary.main' }} />}
                  title="Payment Method"
                  titleTypographyProps={{ fontWeight: 600 }}
                  sx={{ bgcolor: 'grey.200' }}
                />
                <CardContent>
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                        {payment?.method === 'codbased' ? 'Cash on Delivery (COD)' : 'UPI / Online'}
                    </Typography>
                </CardContent>
            </Card>
        );
    };
    
    const renderDeliveryInfo = () => (
        <Card variant="outlined" sx={{ mb: 3 }}>
             <CardHeader 
                avatar={order_type === "delivery" ? <LocalShippingIcon sx={{ color: 'primary.main' }} /> : <StorefrontIcon sx={{ color: 'primary.main' }} />}
                title={order_type === "delivery" ? "Delivery Details" : "Pickup Information"}
                titleTypographyProps={{ fontWeight: 600 }}
                sx={{ bgcolor: 'grey.200' }}
             />
             <CardContent>
                {order_type === "delivery" && shipping ? (
                     <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Shipping to</Typography>
                        <Typography fontWeight={600}>{shipping.name}</Typography>
                        <Typography variant="body2">{shipping.address}, {shipping.city}, {shipping.pincode}</Typography>
                        <Typography variant="body2">{shipping.phone}</Typography>
                     </>
                 ) : (
                     <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Pickup Time</Typography>
                        {delivery_date && <Typography variant="body2">Date: {getLocalDate(delivery_date, DATE_FORMAT)}</Typography>}
                        {delivery_time && <Typography variant="body2">Time: {getTimeSlot(delivery_time)}</Typography>}
                        {!delivery_date && !delivery_time && <Typography variant="body2" color="text.secondary">Pickup time not set.</Typography>}
                     </>
                 )}
             </CardContent>
        </Card>
    );

    const renderOrderItems = () => (
        <Card variant="outlined">
            <CardHeader avatar={<ShoppingCartIcon sx={{ color: 'primary.main' }} />} title="Order Items" titleTypographyProps={{ fontWeight: 600 }} sx={{ bgcolor: 'grey.200' }} />
            <CardContent>
                {products && products.map((item, index) => (
                    <React.Fragment key={index}>
                        <Box display="flex" alignItems="center" my={1.5}>
                            <Avatar variant="rounded" src={getImage(item.product.images)} sx={{ width: 60, height: 60, mr: 2 }} />
                            <Box flexGrow={1}>
                                <Typography fontWeight={600}>{item.product.name}</Typography>
                                <Typography variant="body2" color="text.secondary">{item.quantity} x {symbol}{parseFloat(item.seller_price).toFixed(2)}</Typography>
                                {item.product_size && <Typography variant="caption" color="text.secondary">Size: {item.product_size}</Typography>}
                            </Box>
                            <Typography fontWeight={600}>{symbol}{(item.quantity * parseFloat(item.seller_price)).toFixed(2)}</Typography>
                        </Box>
                        {index < products.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </CardContent>
        </Card>
    );

    const renderOrderSummary = () => (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.200', borderRadius: 2 }}>
            <Grid container spacing={1} justifyContent="flex-end">
                <Grid item xs={6}><Typography color="text.secondary">Subtotal:</Typography></Grid>
                <Grid item xs={6}><Typography align="right">{symbol}{total_price ? parseFloat(total_price).toFixed(2) : '0.00'}</Typography></Grid>
                <Grid item xs={6}><Typography color="text.secondary">Delivery:</Typography></Grid>
                <Grid item xs={6}>
                    <Typography align="right">
                        {order_type === "delivery" && status === "new" && shippingoption === "actualcost"
                    ? "To Be Added"
                    : `${symbol}${total_shipping ? parseFloat(total_shipping).toFixed(2) : '0.00'}`
                }
                </Typography></Grid>
                <Grid item xs={6}><Typography color="text.secondary">Tax:</Typography></Grid>
                <Grid item xs={6}><Typography align="right">{symbol}{total_tax ? parseFloat(total_tax).toFixed(2) : '0.00'}</Typography></Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                <Grid item xs={6}><Typography variant="h6" fontWeight={700}>Total:</Typography></Grid>
                <Grid item xs={6}><Typography variant="h6" fontWeight={700} align="right">{symbol}{total_order ? parseFloat(total_order).toFixed(2) : '0.00'}</Typography></Grid>
                <Grid item xs={12}> {(order_type === "delivery" && status === "new" && shippingoption === "actualcost") && "Pending Delivery Charges - Will be added after order confirmation"}
                </Grid>
            </Grid>
        </Box>
    );

    if (isLoading || !orderdetail.order_id) {
        return <MockOrderDetail />;
    }

    return (
        <Container sx={{ py: 3 }}>
            <Box mb={3} mt={2}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Order #{order_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Placed on: {getLocalDate(createdAt, `${DATE_FORMAT} hh:mm A`)}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    {renderOrderItems()}
                    {renderOrderSummary()}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderStatusAndActions()}
                    {renderDeliveryInfo()}
                </Grid>
            </Grid>

            {/* Payment Screenshot Upload Dialog */}
            <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Payment Screenshot</DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            Upload a screenshot or photo of your payment confirmation
                        </Typography>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={6}>
                                <FileButton onFileSelect={addFileImage} accept="image/*" />
                            </Grid>
                            <Grid item xs={6}>
                                <CameraButton onChange={addCameraImage} />
                            </Grid>
                        </Grid>
                        {uploading && (
                            <Box mt={2}>
                                <CircularProgress size={24} />
                                <Typography variant="caption" display="block" mt={1}>
                                    Uploading...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

ShopOrderDetail.defaultProps = {
    isLoading: false,
    symbol: CURRENCY,
};

ShopOrderDetail.propTypes = {
    isLoading: PropTypes.bool,
    orderdetail: PropTypes.objectOf(PropTypes.any).isRequired,
    match: PropTypes.objectOf(PropTypes.any).isRequired,
    getShopOrderDetail: PropTypes.func.isRequired,
    bootupInfo: PropTypes.objectOf(PropTypes.any).isRequired,
    symbol: PropTypes.string,
};

const mapStateToProps = ({ userorders, bootupInfo }) => ({
    bootupInfo,
    symbol: bootupInfo.symbol,
    shopinfo: bootupInfo.shopinfo,
    reqStatus: userorders.reqStatus,
    isLoading: userorders.isLoading,
    serviceError: userorders.serviceError,
    orderdetail: userorders.orderdetail,
    pendingPayments: userorders.pendingPayments,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...Actions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ShopOrderDetail);
